import React, { useEffect, useMemo, useState } from 'react';
import './LoginPage.css';
import axios from 'axios';
// @ts-ignore
import Cookies from 'js-cookie';
import { constants } from '../../config/constants';
import { zynity_api } from '../../config/external-routes';
import { AuthHeader, defaultAuthMenuItems } from './AuthHeader';
import { SocialLoginButton } from './SocialLoginButton';
import { apiClient } from '../../api/httpClient';
import { useLocation } from 'react-router-dom';
import { getAccessTokenCookieOptions } from '../../helpers/cookieOptions';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socialLoading, setSocialLoading] = useState<'google' | 'microsoft' | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const location = useLocation();
  const redirectStorageKey = constants.post_login_redirect_storage_key;
  const enableGoogleLogin = process.env.REACT_APP_ENABLE_GOOGLE_LOGIN === 'true';
  const enableMicrosoftLogin = process.env.REACT_APP_ENABLE_MICROSOFT_LOGIN === 'true';
  const showSocialLogin = enableGoogleLogin || enableMicrosoftLogin;
  const queryMessage = useMemo(() => new URLSearchParams(location.search).get('msg'), [location.search]);

  useEffect(() => {
    setSuccessMessage(queryMessage);
  }, [queryMessage]);

  const getRedirectPath = () => {
    try {
      const storedPath = localStorage.getItem(redirectStorageKey);
      if (!storedPath) return null;
      if (!storedPath.startsWith('/')) return null;
      if (storedPath === '/' || storedPath === '/login' || storedPath === '/site/login') return null;
      return storedPath;
    } catch {
      return null;
    }
  };

  const clearRedirectPath = () => {
    try {
      localStorage.removeItem(redirectStorageKey);
    } catch {
      // ignore storage errors
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'microsoft') => {
    if (
      (provider === 'google' && !enableGoogleLogin) ||
      (provider === 'microsoft' && !enableMicrosoftLogin)
    ) {
      return;
    }
    setError(null);
    setSuccessMessage(null);
    setSocialLoading(provider);
    const redirectUri = `${window.location.origin}/auth/social-complete`;

    try {
      const response = await apiClient.get(`/api/oauth/${provider}/redirect`, {
        params: { redirect_uri: redirectUri, mode: 'token' },
      });
      const authUrl = response.data?.auth_url || response.data?.authUrl || response.data?.url;
      if (authUrl) {
        window.location.assign(authUrl);
        return;
      }
      setError('Unable to start sign in. Please try again.');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to start sign in. Please try again.';
      setError(message);
    } finally {
      setSocialLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const response = await axios.post(zynity_api.login, {
        email,
        password,
      });
      const { access_token } = response.data || {};
      if (access_token) {
        Cookies.set(
          constants.zynity_api_access_token_cookie_name,
          access_token,
          getAccessTokenCookieOptions()
        );
        const redirectPath = getRedirectPath();
        clearRedirectPath();
        window.location.replace(redirectPath || '/');
        return;
      }
      setError('Unable to sign in. Please try again.');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Unable to sign in. Please check your credentials.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting || Boolean(socialLoading);

  return (
    <div className="login-page">
      <AuthHeader menuItems={defaultAuthMenuItems} />

      <main className="login-content">
        <h1 className="login-title">LOGIN</h1>
        <section className="login-card" aria-label="Login form">
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="input-row">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div className="input-row">
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            {successMessage && <div className="login-success" role="status">{successMessage}</div>}
            {error && <div className="login-error" role="alert">{error}</div>}
            <button type="submit" className="sign-in-btn" disabled={isBusy}>
              {isSubmitting ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
            {showSocialLogin && (
              <>
                <div className="social-login-divider" aria-hidden="true">
                  <span className="line" />
                  <span className="divider-label">or</span>
                  <span className="line" />
                </div>
                <div className="social-login-buttons">
                  {enableGoogleLogin && (
                    <SocialLoginButton
                      provider="google"
                      label="Continue with Google"
                      loading={socialLoading === 'google'}
                      disabled={isSubmitting}
                      onClick={() => handleSocialLogin('google')}
                    />
                  )}
                  {enableMicrosoftLogin && (
                    <SocialLoginButton
                      provider="microsoft"
                      label="Continue with Microsoft"
                      loading={socialLoading === 'microsoft'}
                      disabled={isSubmitting}
                      onClick={() => handleSocialLogin('microsoft')}
                    />
                  )}
                </div>
              </>
            )}
            <div className="password-row">
              <a className="reset-password" href="/site/loginhelp">
                Reset Password <span className="info-icon">i</span>
              </a>
            </div>
            <div className="signup-row">
              <span>Don't have a Zynity account?</span>
              <a className="signup-link" href="/people/register">
                Sign up now.
              </a>
            </div>
          </form>
        </section>
        <p className="login-footer">Copyright © 2015-2025 Zynity. All rights reserved.</p>
      </main>
    </div>
  );
};
